import { Injectable } from '@angular/core';
import { 
  UnifiedPropertyFinancials, 
  UnifiedContractFinancials, 
  UnifiedLoanFinancials,
  TemporalFinancialAmount
} from '@shared/definitions/unified-financial-data.model';
import {
  createTemporalAmount
} from '@shared/definitions/unified-financial-data.model';
import { DataValidationService } from '@core/services/finance/data-validation.service';

// Import existing services
import { PropertyStore } from '@shared/services/property.store';
import { ContractStore } from '@shared/services/contract.store';
import { LoanStore } from '@shared/services/loan.store';

// Import existing models
import { Property } from '@shared/definitions/property';
import { Contract } from '@shared/definitions/contract';
import { Loan, PaymentPeriod } from '@shared/definitions/loan';

// Expense rows coming from Firestore/store haven't been promoted to a first-class
// model yet — kept narrow so the `extractExpenseByType` helper has a typed shape.
interface ExpenseLike {
  type?: string;
  category?: string;
  amount?: number;
}

/**
 * DATA NORMALIZATION SERVICE
 * 
 * Converts raw property, contract, and loan data into standardized 
 * UnifiedFinancials models with temporal consistency and data quality assessment.
 * 
 * Key responsibilities:
 * - Extract and normalize data from existing entities
 * - Apply fallback and estimation logic for missing data
 * - Ensure temporal consistency (monthly vs annual)
 * - Calculate data quality scores
 * - Handle missing or inconsistent source data
 */
@Injectable({
  providedIn: 'root'
})
export class DataNormalizationService {
  
  constructor(
    private validationService: DataValidationService,
    private propertyStore: PropertyStore,
    private contractStore: ContractStore,
    private loanStore: LoanStore
  ) {}
  
  /**
   * MAIN METHOD: Normalize property data from raw sources
   * This is the primary entry point for getting standardized property financial data
   */
  async normalizePropertyFinancials(propertyId: string): Promise<UnifiedPropertyFinancials> {
    try {
      // Load raw data synchronously from stores
      const property = this.propertyStore.getById(propertyId);
      const contracts = [...this.contractStore.getForProperty(propertyId)];
      const loans = [...this.loanStore.getForProperty(propertyId)];
      
      if (!property) {
        throw new Error(`Property ${propertyId} not found`);
      }
      
      console.log(`[DataNormalizationService] Normalizing data for property ${propertyId}:`, {
        contractsFound: contracts.length,
        loansFound: loans.length,
        hasFinancials: !!property.financials,
        activeContracts: contracts.filter(c => c.status === 'Active').length,
        totalLoanAmount: loans.reduce((sum, loan) => {
          const amount = typeof loan.capital === 'object' ? loan.capital.nominalAmount : (loan.capital || 0);
          return sum + amount;
        }, 0)
      });
      
      // Extract and normalize each component
      const currentRent = this.extractCurrentRent(property, contracts);
      const realContractRent = this.extractRealContractRent(property, contracts); // ✅ STRICT MODE: Real contract rent only
      const propertyValue = this.extractPropertyValue(property);
      const operatingExpenses = this.extractOperatingExpenses(property);
      const growthRates = this.extractGrowthRates(property);
      const occupancyData = this.extractOccupancyData(property, contracts);
      
      // Log detailed extraction results
      console.log(`[DataNormalizationService] Data extraction summary for ${propertyId}:`, {
        currentRent: { amount: currentRent.amount, source: currentRent.source, confidence: currentRent.confidence },
        realContractRent: { amount: realContractRent.amount, source: realContractRent.source, confidence: realContractRent.confidence },
        propertyValue: { amount: propertyValue.amount, source: propertyValue.source },
        operatingExpenses: {
          propertyTax: operatingExpenses.propertyTax.amount,
          insurance: operatingExpenses.insurance.amount,
          maintenance: operatingExpenses.maintenance.amount
        },
        loanData: loans.length > 0 ? {
          totalLoans: loans.length,
          totalOriginalAmount: loans.reduce((sum, loan) => {
            const amount = typeof loan.capital === 'object' ? loan.capital.nominalAmount : (loan.capital || 0);
            return sum + amount;
          }, 0)
        } : 'No loans found'
      });
      
      // Convert property.updatedAt to proper Date object for validation
      let propertyLastUpdated: Date;
      try {
        // Firestore may return `updatedAt` as Timestamp, Date, string, or number.
        const updatedAt = property.updatedAt as unknown as { toDate?: () => Date } | Date | string | number | null | undefined;
        if (updatedAt && typeof updatedAt === 'object' && 'toDate' in updatedAt && typeof updatedAt.toDate === 'function') {
          // Firestore Timestamp
          propertyLastUpdated = (updatedAt as { toDate: () => Date }).toDate();
        } else if (updatedAt instanceof Date) {
          // JavaScript Date
          propertyLastUpdated = updatedAt;
        } else if (typeof updatedAt === 'string' || typeof updatedAt === 'number') {
          // String or number
          propertyLastUpdated = new Date(updatedAt);
        } else {
          // Fallback to current date
          propertyLastUpdated = new Date();
        }
      } catch (dateError) {
        console.warn('[DataNormalizationService] Failed to parse property.updatedAt, using current date:', dateError);
        propertyLastUpdated = new Date();
      }
      
      // Calculate data quality
      const qualityResult = this.validationService.calculateDataQuality({
        baseRent: currentRent,
        currentValue: propertyValue,
        lastUpdated: propertyLastUpdated,
        propertyTax: operatingExpenses.propertyTax,
        insurance: operatingExpenses.insurance,
        rentGrowthRate: growthRates.rentGrowthRate
      });
      
      const normalizedData: UnifiedPropertyFinancials = {
        propertyId,
        currentValue: propertyValue,
        purchasePrice: this.extractPurchasePrice(property),
        purchaseDate: property.financials?.purchaseInfo?.date || undefined, // Used for pro-rata calculations (convert null to undefined)
        baseRent: currentRent,
        actualContractRent: realContractRent, // ✅ REAL contract rent ONLY (for portfolio aggregation)
        additionalRent: this.extractAdditionalRent(contracts),
        otherIncome: this.extractOtherIncome(property),
        ...operatingExpenses,
        ...growthRates,
        ...occupancyData,
        dataQuality: qualityResult.quality,
        lastUpdated: new Date(),
        calculatedBy: 'DataNormalizationService'
      };
      
      return normalizedData;
      
    } catch (error) {
      console.error(`[DataNormalizationService] Error normalizing property ${propertyId}:`, error);
      throw new Error(`Failed to normalize property data: ${error instanceof Error ? error.message : 'Unknown error'}`, { cause: error });
    }
  }
  
  /**
   * Extract rental income from ACTIVE CONTRACTS ONLY (strict mode)
   * Used for portfolio aggregation to show ONLY real rented income
   * Returns 0 if no active contract exists
   */
  public extractRealContractRent(
    property: Property,
    contracts: Contract[]
  ): TemporalFinancialAmount {
    console.log(`[DataNormalizationService] 🔍 STRICT MODE: Extracting REAL contract rent for ${property.propertyUID}`);
    
    // Priority 1: Active contracts ONLY
    const activeContract = contracts.find(c => c.status === 'Active');
    if (activeContract?.contractFinancials) {
      const monthlyRent = activeContract.contractFinancials.getCurrentMonthlyRentEquivalent?.() || 
                         activeContract.contractFinancials.getMonthlyRentEquivalent?.();
      
      if (monthlyRent && monthlyRent > 0) {
        return createTemporalAmount(monthlyRent, 'monthly', 'EUR', 'actual', 1.0);
      }
      
      // Fallback: manual calculation from contract
      if (activeContract.contractFinancials.baseRentAmount && activeContract.contractFinancials.rentFrequency) {
        const manualMonthlyRent = this.calculateMonthlyEquivalent(
          activeContract.contractFinancials.baseRentAmount, 
          activeContract.contractFinancials.rentFrequency
        );
        
        if (manualMonthlyRent > 0) {
          return createTemporalAmount(manualMonthlyRent, 'monthly', 'EUR', 'actual', 1.0);
        }
      }
    }
    
    // NO active contract = ZERO rent (strict mode)
    return createTemporalAmount(0, 'monthly', 'EUR', 'actual', 0);
  }
  
  /**
   * Extract current rental income with priority system (with fallbacks)
   * Used for property-level financial calculations where projections are acceptable
   */
  private extractCurrentRent(
    property: Property, 
    contracts: Contract[]
  ): TemporalFinancialAmount {
    
    console.log(`[DataNormalizationService] Extracting rental data for property ${property.propertyUID}:`, {
      totalContracts: contracts.length,
      activeContracts: contracts.filter(c => c.status === 'Active').length,
      contractStatuses: contracts.map(c => ({
        id: c.contractUID, 
        status: c.status, 
        baseRentAmount: c.contractFinancials?.baseRentAmount,
        rentFrequency: c.contractFinancials?.rentFrequency,
        monthlyEquivalent: c.contractFinancials?.getCurrentMonthlyRentEquivalent?.() || c.contractFinancials?.getMonthlyRentEquivalent?.()
      })),
      userExpectedRent: property.financials?.parameters?.baseExpectedRent
    });
    
    // Priority 0: Effective payments (actual received payments - highest priority)
    // TODO: Implement effective payment tracking when available
    // if (effectivePayments.length > 0) { ... }
    
    // Priority 1: Active contracts (actual contractual obligations - highest current priority)
    const activeContract = contracts.find(c => c.status === 'Active');
    if (activeContract?.contractFinancials) {
      // Use proper calculation methods for real rent amounts divided by periods
      const monthlyRent = activeContract.contractFinancials.getCurrentMonthlyRentEquivalent?.() || 
                         activeContract.contractFinancials.getMonthlyRentEquivalent?.();
      
      if (monthlyRent && monthlyRent > 0) {
        console.log(`[DataNormalizationService] ✅ Using ACTIVE CONTRACT rent calculation:`, {
          contractUID: activeContract.contractUID,
          baseRentAmount: activeContract.contractFinancials.baseRentAmount,
          rentFrequency: activeContract.contractFinancials.rentFrequency,
          monthlyEquivalent: monthlyRent,
          calculationMethod: 'getCurrentMonthlyRentEquivalent() or getMonthlyRentEquivalent()'
        });
        
        return createTemporalAmount(
          monthlyRent,
          'monthly',
          'EUR',
          'actual',
          1.0
        );
      }
      
      // Fallback: if contract has baseRentAmount but no calculation methods, calculate manually
      if (activeContract.contractFinancials.baseRentAmount && activeContract.contractFinancials.rentFrequency) {
        const manualMonthlyRent = this.calculateMonthlyEquivalent(
          activeContract.contractFinancials.baseRentAmount, 
          activeContract.contractFinancials.rentFrequency
        );
        
        if (manualMonthlyRent > 0) {
          console.log(`[DataNormalizationService] ✅ Using MANUAL CALCULATION for contract rent:`, {
            contractUID: activeContract.contractUID,
            baseRentAmount: activeContract.contractFinancials.baseRentAmount,
            rentFrequency: activeContract.contractFinancials.rentFrequency,
            manualMonthlyEquivalent: manualMonthlyRent,
            calculationMethod: 'manual baseRentAmount / frequency calculation'
          });
          
          return createTemporalAmount(
            manualMonthlyRent,
            'monthly',
            'EUR',
            'actual',
            1.0
          );
        }
      }
    }
    
    // Priority 2: Property rental data (medium confidence)
    if (property.rental?.currentRent && property.rental.currentRent > 0) {
      return createTemporalAmount(
        property.rental.currentRent,
        'monthly',
        'EUR',
        'actual',
        0.9 // Slightly lower confidence if not from active contract
      );
    }
    
    // Priority 3: User-defined expected rent (fallback when no real data available)
    if (property.financials?.parameters?.baseExpectedRent && property.financials.parameters.baseExpectedRent > 0) {
      console.log(`[DataNormalizationService] 📝 Using USER EXPECTED RENT: €${property.financials.parameters.baseExpectedRent}/month (user setting as fallback)`);
      return createTemporalAmount(
        property.financials.parameters.baseExpectedRent,
        'monthly',
        'EUR',
        'projected', // This is a user projection/expectation
        0.7 // Lower confidence as it's user-defined, not actual data
      );
    }
    
    // Priority 4: Initial rent from property (lower confidence)
    if (property.rental?.initialRent && property.rental.initialRent > 0) {
      return createTemporalAmount(
        property.rental.initialRent,
        'monthly',
        'EUR',
        'actual',
        0.8 // Lower confidence as it might be outdated
      );
    }
    
    // Priority 5: Estimate based on property value (5% annual yield assumption)
    const propertyValue = property.financials?.currentValue || 0;
    if (propertyValue > 0) {
      const estimatedAnnualRent = propertyValue * 0.05; // 5% gross yield assumption
      const estimatedMonthlyRent = estimatedAnnualRent / 12;
      console.log(`[DataNormalizationService] 📊 ESTIMATING rent based on property value (5% yield): €${estimatedMonthlyRent.toFixed(0)}/month`);
      return createTemporalAmount(
        estimatedMonthlyRent,
        'monthly',
        'EUR',
        'estimated',
        0.3 // Low confidence for estimated data
      );
    }
    
    // Fallback: Zero with very low confidence. Debug-level on purpose — this is
    // an expected outcome for properties with no rent data yet (fires per
    // property, so `warn` flooded the prod console); downstream consumers must
    // guard against the resulting 0 amount rather than rely on this log.
    console.debug(`[DataNormalizationService] ❌ NO RENTAL DATA found for property, using zero`);
    return createTemporalAmount(0, 'monthly', 'EUR', 'estimated', 0.1);
  }
  
  /**
   * Calculate monthly equivalent for any amount and frequency
   * @param amount The amount in the original frequency
   * @param frequency The payment frequency
   * @returns Monthly equivalent amount
   */
  private calculateMonthlyEquivalent(amount: number, frequency: string | undefined | null): number {
    if (!amount) return 0;
    
    switch (frequency) {
      case 'monthly':
        return amount;
      case 'quarterly':
        return amount / 3;
      case 'annually':
        return amount / 12;
      default:
        return amount; // Default to monthly if frequency is unknown
    }
  }
  
  /**
   * Extract property value with fallback logic
   */
  private extractPropertyValue(property: Property): TemporalFinancialAmount {
    
    // Priority 1: Current market value
    if (property.financials?.currentValue && property.financials.currentValue > 0) {
      return createTemporalAmount(
        property.financials.currentValue,
        'annually', // Property values are typically annual assessments
        'EUR',
        'actual', // Assume current if recently updated
        0.8 // Medium confidence for market valuations
      );
    }
    
    // Priority 2: Purchase price as the standing proxy for current value.
    // Product rule: when `currentValue` is not set, the property value IS the
    // purchase price — unconditionally, regardless of the purchase date. This
    // matches the `currentValue || purchaseInfo.price` convention used across
    // the app (financial-setup.helpers, property-detail-overview, the
    // dashboards). An older purchase is slightly less reliable, so confidence
    // tapers with age, but it is never dropped to zero.
    if (property.financials?.purchaseInfo?.price && property.financials.purchaseInfo.price > 0) {
      const purchaseDate = property.financials.purchaseInfo.date;
      const isRecent = !!purchaseDate &&
        (Date.now() - new Date(purchaseDate).getTime()) < (365 * 24 * 60 * 60 * 1000 * 2); // Less than 2 years

      return createTemporalAmount(
        property.financials.purchaseInfo.price,
        'annually',
        'EUR',
        'actual',
        isRecent ? 0.7 : 0.5 // Purchase price proxy; lower confidence when stale
      );
    }

    // Fallback: Zero with very low confidence
    console.warn(`[DataNormalizationService] No property value found for property ${property.propertyUID}`);
    return createTemporalAmount(0, 'annually', 'EUR', 'estimated', 0.1);
  }
  
  /**
   * Extract purchase price information
   */
  private extractPurchasePrice(property: Property): TemporalFinancialAmount {
    if (property.financials?.purchaseInfo?.price) {
      return createTemporalAmount(
        property.financials.purchaseInfo.price,
        'annually', // Purchase price is a one-time annual equivalent
        'EUR',
        'actual',
        1.0 // High confidence for recorded purchase price
      );
    }
    
    return createTemporalAmount(0, 'annually', 'EUR', 'estimated', 0.1);
  }
  
  /**
   * Extract operating expenses with defaults and estimates
   * Now includes pro-rata temporis for first year and real data prioritization
   */
  private extractOperatingExpenses(property: Property): {
    propertyTax: TemporalFinancialAmount;
    insurance: TemporalFinancialAmount;
    maintenance: TemporalFinancialAmount;
    managementFee: TemporalFinancialAmount;
    utilities: TemporalFinancialAmount;
    otherExpenses: TemporalFinancialAmount;
  } {
    
    // Use the proper property value extraction logic instead of direct access
    const propertyValueData = this.extractPropertyValue(property);
    const propertyValue = propertyValueData.amount;
    
    
    // Check if property has configured financial parameters
    const parameters = property.financials?.parameters;
    const useRealDataOnly = parameters?.useRealDataOnly || false;
    const useStandardExpenses = parameters?.assumeStandardExpenses !== false; // Default to true
    
    // Calculate pro-rata factor for first year after purchase
    const proRataFactor = this.calculateProRataFactor(property);
    
    console.log(`[DataNormalizationService] Financial settings for ${property.propertyUID}:`, {
      useRealDataOnly,
      useStandardExpenses,
      hasParameters: !!parameters,
      proRataFactor: proRataFactor,
      parameterKeys: parameters ? Object.keys(parameters) : [],
      fullParameters: parameters,
      annualPropertyTax: parameters?.annualPropertyTax,
      annualInsurance: parameters?.annualInsurance,
      maintenancePercentage: parameters?.maintenancePercentage,
      managementFeePercentage: parameters?.managementFeePercentage,
      otherMonthlyExpenses: parameters?.otherMonthlyExpenses,
      baseExpectedRent: parameters?.baseExpectedRent
    });

    // IMPORTANT: Store FULL ANNUAL amounts, do NOT apply pro-rata here
    // Pro-rata should only be applied in year-specific cashflow calculations
    // Otherwise, projections for future years will use the reduced amounts
    
    // Property Tax (FULL YEAR amount)
    let propertyTaxAmount = 0;
    if (parameters?.annualPropertyTax && parameters.annualPropertyTax > 0) {
      propertyTaxAmount = parameters.annualPropertyTax;
    } else if (useStandardExpenses && !useRealDataOnly) {
      propertyTaxAmount = this.estimatePropertyTax(propertyValue);
    }

    // Insurance (FULL YEAR amount)
    let insuranceAmount = 0;
    if (parameters?.annualInsurance && parameters.annualInsurance > 0) {
      insuranceAmount = parameters.annualInsurance;
    } else if (useStandardExpenses && !useRealDataOnly) {
      insuranceAmount = this.estimateInsurance(propertyValue);
    }

    // Maintenance (FULL YEAR amount)
    let maintenanceAmount = 0;
    if (parameters?.maintenancePercentage && parameters.maintenancePercentage > 0) {
      maintenanceAmount = propertyValue * (parameters.maintenancePercentage / 100);
    } else if (useStandardExpenses && !useRealDataOnly) {
      maintenanceAmount = this.estimateMaintenance(propertyValue);
    }

    // Management Fee (FULL YEAR amount, calculated based on rental income)
    const baseRentData = this.extractCurrentRent(property, []);
    let managementFeeAmount = 0;
    if (parameters?.managementFeePercentage && parameters.managementFeePercentage > 0 && baseRentData.amount > 0) {
      const annualRent = baseRentData.amount * 12;
      managementFeeAmount = annualRent * (parameters.managementFeePercentage / 100);
    } else if (useStandardExpenses && !useRealDataOnly) {
      managementFeeAmount = this.estimateManagementFee(baseRentData.amount * 12);
    }

    // Other Monthly Expenses converted to annual (FULL YEAR amount)
    let otherExpensesAmount = 0;
    if (parameters?.otherMonthlyExpenses && parameters.otherMonthlyExpenses > 0) {
      otherExpensesAmount = parameters.otherMonthlyExpenses * 12;
    } else if (useStandardExpenses && !useRealDataOnly) {
      otherExpensesAmount = this.estimateUtilities();
    }
    
    // Note: Pro-rata factor is calculated but not applied here
    // It will be used by cashflow projection methods for first-year calculations

    return {
      propertyTax: createTemporalAmount(propertyTaxAmount, 'annually', 'EUR', 
        parameters?.annualPropertyTax ? 'projected' : 'estimated', 
        parameters?.annualPropertyTax ? 0.9 : 0.5),
      insurance: createTemporalAmount(insuranceAmount, 'annually', 'EUR', 
        parameters?.annualInsurance ? 'projected' : 'estimated', 
        parameters?.annualInsurance ? 0.9 : 0.5),
      maintenance: createTemporalAmount(maintenanceAmount, 'annually', 'EUR', 
        parameters?.maintenancePercentage ? 'projected' : 'estimated', 
        parameters?.maintenancePercentage ? 0.9 : 0.5),
      managementFee: createTemporalAmount(managementFeeAmount, 'annually', 'EUR', 
        parameters?.managementFeePercentage ? 'projected' : 'estimated', 
        parameters?.managementFeePercentage ? 0.9 : 0.5),
      utilities: createTemporalAmount(0, 'annually', 'EUR', 'estimated', 0.3),
      otherExpenses: createTemporalAmount(otherExpensesAmount, 'annually', 'EUR', 
        parameters?.otherMonthlyExpenses ? 'projected' : 'estimated', 
        parameters?.otherMonthlyExpenses ? 0.9 : 0.3)
    };
  }
  
  /**
   * PUBLIC: Calculate pro-rata factor for a specific year given a purchase date
   * This is the SINGLE SOURCE OF TRUTH for pro-rata calculations
   * 
   * @param purchaseDate The date the property was purchased
   * @param targetYear The calendar year to calculate pro-rata for
   * @returns Factor between 0 and 1 (1.0 = full year, <1.0 = partial year)
   */
  public calculateProRataFactorForYear(
    purchaseDate: Date | string | { toDate: () => Date } | null | undefined,
    targetYear: number,
  ): number {
    if (!purchaseDate) {
      return 1.0; // No purchase date = full year
    }

    // Firestore roundtrips can surface the value as a Timestamp wrapper or
    // an ISO string rather than a Date. Coerce defensively so downstream
    // `getFullYear()` / comparisons don't throw.
    let normalizedDate: Date;
    if (purchaseDate instanceof Date) {
      normalizedDate = purchaseDate;
    } else if (typeof (purchaseDate as { toDate?: () => Date }).toDate === 'function') {
      normalizedDate = (purchaseDate as { toDate: () => Date }).toDate();
    } else {
      normalizedDate = new Date(purchaseDate as string);
    }
    if (Number.isNaN(normalizedDate.getTime())) {
      return 1.0;
    }
    purchaseDate = normalizedDate;

    const purchaseYear = purchaseDate.getFullYear();
    
    // Only apply pro-rata to the purchase year
    if (targetYear !== purchaseYear) {
      return 1.0; // Full year for all years except purchase year
    }
    
    // Calculate days owned in the purchase year
    const yearStart = new Date(purchaseYear, 0, 1);
    const yearEnd = new Date(purchaseYear, 11, 31, 23, 59, 59);
    
    // If purchased before or on Jan 1, it's a full year
    if (purchaseDate <= yearStart) {
      return 1.0;
    }
    
    // If purchased after Dec 31, something is wrong
    if (purchaseDate > yearEnd) {
      console.warn(`[DataNormalizationService] Purchase date ${purchaseDate} is after target year ${targetYear}`);
      return 0;
    }
    
    // Calculate proportion of year owned
    const totalDays = Math.ceil((yearEnd.getTime() - yearStart.getTime()) / (1000 * 60 * 60 * 24));
    const ownedDays = Math.ceil((yearEnd.getTime() - purchaseDate.getTime()) / (1000 * 60 * 60 * 24));
    const proRataFactor = ownedDays / totalDays;
    
    
    return proRataFactor;
  }
  
  /**
   * PRIVATE: Calculate pro-rata factor for expenses based on property purchase date
   * Uses the current year as target year
   * NOTE: This is only used internally during data normalization
   */
  private calculateProRataFactor(property: Property): number {
    if (!property.financials?.purchaseInfo?.date) {
      return 1.0; // Default to full year if no purchase date
    }
    
    try {
      const purchaseDate = new Date(property.financials.purchaseInfo.date);
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const purchaseYear = purchaseDate.getFullYear();
      
      // Only apply pro-rata for the current year or first year after purchase
      if (purchaseYear === currentYear) {
        // Calculate days remaining in purchase year
        const yearEnd = new Date(purchaseYear, 11, 31); // December 31st
        const daysFromPurchase = Math.ceil((yearEnd.getTime() - purchaseDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
        const totalDaysInYear = Math.ceil((yearEnd.getTime() - new Date(purchaseYear, 0, 1).getTime()) / (1000 * 60 * 60 * 24)) + 1;
        const proRataFactor = daysFromPurchase / totalDaysInYear;
        
        console.log(`[DataNormalizationService] Pro-rata calculation for purchase year ${purchaseYear}:`, {
          purchaseDate: purchaseDate.toISOString().substring(0, 10),
          daysFromPurchase,
          totalDaysInYear,
          proRataFactor: proRataFactor.toFixed(3)
        });
        
        return proRataFactor;
      } else {
        // For years after purchase year, use full year
        return 1.0;
      }
    } catch (error) {
      console.error(`[DataNormalizationService] Error calculating pro-rata factor:`, error);
      return 1.0; // Default to full year on error
    }
  }
  
  /**
   * Extract expense by type from expenses array
   * Note: PropertyFinancials doesn't have expenses, so this is for future compatibility
   */
  private extractExpenseByType(expenses: ExpenseLike[] | undefined | null, type: string): number {
    if (!expenses || !Array.isArray(expenses)) return 0;
    const expense = expenses.find(e => e.type === type || e.category === type);
    return expense?.amount || 0;
  }
  
  /**
   * Estimate property tax (default: 0.3% of property value annually)
   */
  private estimatePropertyTax(propertyValue: number): number {
    const tax = propertyValue * 0.003; // 0.3% of property value
    return tax;
  }
  
  /**
   * Estimate insurance (default: 0.15% of property value annually)
   */
  private estimateInsurance(propertyValue: number): number {
    const insurance = propertyValue * 0.0015; // 0.15% of property value
    return insurance;
  }
  
  /**
   * Estimate maintenance (default: 1% of property value annually)
   */
  private estimateMaintenance(propertyValue: number): number {
    const maintenance = propertyValue * 0.01; // 1% of property value
    return maintenance;
  }
  
  /**
   * Estimate utilities (default: €800 annually for residential)
   */
  private estimateUtilities(): number {
    const utilities = 800; // Default estimate
    return utilities;
  }
  
  /**
   * Estimate management fee (default: 8% of annual rental income)
   */
  private estimateManagementFee(annualRentalIncome: number): number {
    const fee = annualRentalIncome * 0.08; // 8% of rental income
    return fee;
  }
  
  /**
   * Extract growth rates with defaults
   */
  private extractGrowthRates(_property: Property): {
    rentGrowthRate: number;
    expenseGrowthRate: number;
    propertyGrowthRate: number;
  } {
    // TODO: Extract from property data if available
    // For now, use conservative defaults
    return {
      rentGrowthRate: 2.5, // 2.5% annual rent growth
      expenseGrowthRate: 2.0, // 2% annual expense growth
      propertyGrowthRate: 3.0 // 3% annual property appreciation
    };
  }
  
  /**
   * Extract occupancy data and vacancy rate
   */
  private extractOccupancyData(property: Property, contracts: Contract[]): {
    vacancyRate: number;
    occupancyHistory: import('@shared/definitions/unified-financial-data.model').OccupancyRecord[];
  } {
    
    // Calculate current vacancy based on active contracts
    const hasActiveContract = contracts.some(c => c.status === 'Active');
    const currentVacancyRate = hasActiveContract ? 0 : 100; // 0% if rented, 100% if vacant
    
    // Use property-specific vacancy rate if available, otherwise use current status
    const vacancyRate = property.rental?.isRented === false ? 
      currentVacancyRate : 
      5; // Default 5% vacancy assumption for rented properties
    
    return {
      vacancyRate,
      occupancyHistory: [] // TODO: Extract from rental history if available
    };
  }
  
  /**
   * Extract additional rental income
   */
  private extractAdditionalRent(contracts: Contract[]): TemporalFinancialAmount | undefined {
    const activeContract = contracts.find(c => c.status === 'Active');
    
    if (activeContract?.contractFinancials?.fixedChargesAmount && 
        activeContract.contractFinancials.fixedChargesAmount > 0) {
      return createTemporalAmount(
        activeContract.contractFinancials.fixedChargesAmount,
        'monthly',
        'EUR',
        'actual',
        0.9
      );
    }
    
    return undefined;
  }
  
  /**
   * Extract other income sources
   */
  private extractOtherIncome(_property: Property): TemporalFinancialAmount | undefined {
    // TODO: Extract from property data if available (parking, storage, etc.)
    return undefined;
  }
  
  /**
   * Normalize contract data into unified format
   */
  normalizeContractFinancials(contract: Contract): UnifiedContractFinancials {
    const monthlyBaseRent = createTemporalAmount(
      contract.contractFinancials?.monthlyBaseRent || 0,
      'monthly',
      'EUR',
      'actual',
      contract.contractFinancials?.monthlyBaseRent ? 1.0 : 0.1
    );
    
    return {
      contractId: contract.contractUID,
      propertyId: contract.propertyUID,
      monthlyBaseRent,
      monthlyAdditionalCharges: contract.contractFinancials?.fixedChargesAmount ? 
        createTemporalAmount(contract.contractFinancials.fixedChargesAmount, 'monthly') : 
        undefined,
      securityDeposit: createTemporalAmount(
        contract.contractFinancials?.depositAmount || 0,
        'monthly' // Deposit is typically 1-3 months rent
      ),
      startDate: contract.contractDates?.startDate || new Date(),
      endDate: contract.contractDates?.endDate || new Date(),
      renewalTerms: {
        automaticRenewal: contract.contractDates?.isAutoRenewable || false,
        renewalPeriod: contract.contractDates?.renewalTerm,
        noticeRequired: contract.contractDates?.tenantNoticePeriodMonths ? 
          contract.contractDates.tenantNoticePeriodMonths * 30 : undefined,
        rentIncrease: undefined // TODO: Extract from contract
      },
      indexationRules: [], // TODO: Extract from contract
      paymentFrequency: 'monthly' as const,
      paymentDay: 1, // Default assumption
      tenantPaysExpenses: {
        propertyTax: false, // Default assumptions
        insurance: false,
        utilities: true,
        maintenance: false,
        commonCharges: true
      },
      dataQuality: monthlyBaseRent.amount > 0 ? ('high' as const) : ('low' as const),
      lastUpdated: new Date()
    };
  }
  
  /**
   * Normalize loan data into unified format
   */
  normalizeLoanFinancials(loan: Loan): UnifiedLoanFinancials {
    console.log(`[DataNormalizationService] 🔍 Normalizing loan ${loan.loanUID}:`, {
      hasLoanTable: !!loan.loanTable,
      loanTableLength: loan.loanTable?.length || 0,
      firstEntry: loan.loanTable?.[0],
      capital: loan.capital,
      interest: loan.interest,
      creditCost: loan.creditCost,
      numberOfMonths: loan.numberOfMonths,
      fullLoanStructure: loan
    });
    
    // DETAILED DEBUGGING: Let's examine the loan table structure
    if (loan.loanTable && loan.loanTable.length > 0) {
      const firstEntry = loan.loanTable[0];
      const sampleMidEntry = loan.loanTable[Math.floor(loan.loanTable.length / 2)];
      const lastEntry = loan.loanTable[loan.loanTable.length - 1];
      
      console.log(`[DataNormalizationService] 🔍 DETAILED loan table analysis for ${loan.loanUID}:`, {
        firstEntry: {
          allKeys: Object.keys(firstEntry),
          period: firstEntry.period,
          date: firstEntry.date,
          startingPrincipal: firstEntry.startingPrincipal,
          interest: firstEntry.interest,
          principalRepayment: firstEntry.principalRepayment,
          fees: firstEntry.fees,
          totalCashout: firstEntry.totalCashout,
          endingPrincipal: firstEntry.endingPrincipal,
          fullEntry: firstEntry
        },
        sampleMidEntry: {
          period: sampleMidEntry.period,
          totalCashout: sampleMidEntry.totalCashout,
          interest: sampleMidEntry.interest,
          principalRepayment: sampleMidEntry.principalRepayment,
          fees: sampleMidEntry.fees,
          fullEntry: sampleMidEntry
        },
        lastEntry: {
          period: lastEntry.period,
          totalCashout: lastEntry.totalCashout,
          endingPrincipal: lastEntry.endingPrincipal,
          fullEntry: lastEntry
        },
        possiblePaymentCalculation: {
          interestPlusPrincipal: (firstEntry.interest || 0) + (firstEntry.principalRepayment || 0),
          totalCashoutPlusFees: (firstEntry.totalCashout || 0) + (firstEntry.fees || 0),
          allFieldsSum: (firstEntry.interest || 0) + (firstEntry.principalRepayment || 0) + (firstEntry.fees || 0)
        }
      });
    }
    
    // Per-period payment: take the FIRST real amortization entry (period > 0;
    // period 0 carries only the opening fees). With the amortization engine now
    // honoring the loan's payment frequency, this entry's principal+interest is
    // exactly ONE payment-period's debt service.
    const firstPayment = (loan.loanTable && loan.loanTable.length > 1)
      ? loan.loanTable.find(entry => Number(entry.period) > 0)
      : undefined;
    const periodicPayment = firstPayment
      ? (firstPayment.principalRepayment || 0) + (firstPayment.interest || 0)
      : 0;

    // Map the loan's payment frequency to the temporal-amount period so that
    // convertToAnnualAmount() annualizes with the correct multiplier
    // (monthly×12, quarterly×4, yearly×1) instead of always assuming monthly.
    const paymentFrequency: 'monthly' | 'quarterly' | 'annually' =
      loan.interest?.interestPaymentPeriod === PaymentPeriod.Quarterly ? 'quarterly'
      : loan.interest?.interestPaymentPeriod === PaymentPeriod.Yearly ? 'annually'
      : 'monthly';

    // Handle the loan.capital which is an object, not a number
    const originalAmount = typeof loan.capital === 'object' ? 
      loan.capital.nominalAmount : 
      (loan.capital || 0);
    
    // Handle the loan.interest which is an object, not a number
    const interestRate = typeof loan.interest === 'object' ? 
      loan.interest.rate : 
      (loan.interest || 0);
    
    // Handle the loan.creditCost which is an object, not a number
    const originationFees = typeof loan.creditCost === 'object' ? 
      loan.creditCost.openingFees : 
      (loan.creditCost || 0);
    
    // Handle Timestamp conversion to Date
    const startDate = loan.startDate && typeof loan.startDate.toDate === 'function' ? 
      loan.startDate.toDate() : 
      new Date();
    
    const result = {
      loanId: loan.loanUID,
      propertyId: loan.propertyUID,
      originalAmount: createTemporalAmount(originalAmount, 'annually'),
      currentBalance: createTemporalAmount(
        loan.loanTable && loan.loanTable.length > 0 ?
          loan.loanTable[loan.loanTable.length - 1].endingPrincipal : originalAmount,
        'annually'
      ),
      interestRate: interestRate * 100, // Convert to percentage
      termYears: loan.numberOfMonths ? loan.numberOfMonths / 12 : 0,
      paymentFrequency,
      regularPayment: createTemporalAmount(periodicPayment, paymentFrequency),
      principalPayment: createTemporalAmount(firstPayment?.principalRepayment || 0, paymentFrequency),
      interestPayment: createTemporalAmount(firstPayment?.interest || 0, paymentFrequency),
      startDate: startDate,
      maturityDate: new Date(), // TODO: Calculate from start date + term
      amortizationSchedule: loan.loanTable?.map((entry, index) => ({
        paymentNumber: index + 1,
        paymentDate: entry.date || new Date(),
        principalPayment: entry.principalRepayment || 0,
        interestPayment: entry.interest || 0,
        totalPayment: (entry.principalRepayment || 0) + (entry.interest || 0),
        remainingBalance: entry.endingPrincipal || 0
      })) || [],
      originationFees: createTemporalAmount(originationFees, 'annually'),
      monthlyFees: undefined,
      loanType: 'fixed' as const, // Default assumption
      status: 'active' as const, // Default assumption
      dataQuality: (originalAmount > 0 ? ('high' as const) : ('low' as const)),
      lastUpdated: new Date()
    };
    
    console.log(`[DataNormalizationService] ✅ Normalized loan ${loan.loanUID}:`, {
      originalAmount: result.originalAmount.amount,
      regularPayment: result.regularPayment.amount,
      interestRate: result.interestRate,
      termYears: result.termYears,
      dataQuality: result.dataQuality
    });
    
    return result;
  }

  /**
   * Extract yearly loan payments directly from loan table data using existing Loan methods
   * This leverages the proven Loan.getCashFlowForPeriod() method that already works
   */
  extractYearlyLoanPayments(loan: Loan): Record<number, number> {
    const yearlyPayments: Record<number, number> = {};
    
    if (!loan.loanTable || loan.loanTable.length === 0) {
      console.log(`[DataNormalizationService] 📊 No loan table data for loan ${loan.loanUID}`);
      return yearlyPayments;
    }
    
    console.log(`[DataNormalizationService] 📊 Extracting yearly payments using Loan.getCashFlowForPeriod() for ${loan.loanUID}:`, {
      loanTableEntries: loan.loanTable.length,
      firstEntryDate: loan.loanTable[0]?.date,
      lastEntryDate: loan.loanTable[loan.loanTable.length - 1]?.date
    });
    
    // Get the date range from the loan table
    const firstEntryDate = new Date(loan.loanTable[0].date);
    const lastEntryDate = new Date(loan.loanTable[loan.loanTable.length - 1].date);
    
    const startYear = firstEntryDate.getFullYear();
    const endYear = lastEntryDate.getFullYear();
    
    // Use the existing Loan.getCashFlowForPeriod method for each year
    for (let year = startYear; year <= endYear; year++) {
      const yearStart = new Date(year, 0, 1); // January 1st
      const yearEnd = new Date(year, 11, 31, 23, 59, 59); // December 31st
      
      // Use the proven Loan method that already works
      const yearlyPayment = Math.abs(loan.getCashFlowForPeriod(yearStart, yearEnd));
      
      if (yearlyPayment > 0) {
        yearlyPayments[year] = yearlyPayment;
        console.log(`[DataNormalizationService] 💰 Year ${year}: €${yearlyPayment.toLocaleString()} (using Loan.getCashFlowForPeriod)`);
      }
    }
    
    return yearlyPayments;
  }
} 